/**
 * Universal share helper.
 *
 * On Capacitor native (iOS/Android) → opens the OS Share sheet (Messages, WhatsApp, Twitter, etc.)
 * via @capacitor/share — this is the same UX as native social apps.
 * On modern browsers (mobile Safari, mobile Chrome) → uses Web Share API (`navigator.share`).
 * Otherwise → copies the link to the clipboard and toasts "Lien copié".
 *
 * All shareable URLs point to the PRODUCTION domain so they resolve from any browser
 * (the preview / dev URLs are blocked from public access).
 */
import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';
import { toast } from 'sonner';

// Always link to the production app; preview URLs are NOT publicly resolvable.
export const PUBLIC_APP_URL = 'https://app.destine.studio';

/**
 * Build a public deep link for a given content type.
 * The path goes through the backend share landing page (`/api/share/...`)
 * which serves OG meta tags + auto-redirects to the in-app deep link.
 * This way crawlers (WhatsApp, Twitter, Facebook, iMessage) get a beautiful
 * unfurl preview, while real users land on the right page.
 */
export const buildShareUrl = (kind, id) => {
  if (!id) return PUBLIC_APP_URL;
  const k = (() => {
    switch (kind) {
      case 'gim_toli':
        return 'gim_toli';
      case 'short':
      case 'video':
        return 'short';
      case 'podcast':
        return 'podcast';
      case 'photo':
        return 'photo';
      case 'video_post':
        return 'video_post';
      case 'studio_story':
        return 'studio_story';
      case 'article':
      case 'news':
        return 'article';
      default:
        return null;
    }
  })();
  if (!k) return `${PUBLIC_APP_URL}/${id}`;
  return `${PUBLIC_APP_URL}/api/share/${k}/${id}`;
};

/**
 * Direct in-app deep link (no OG indirection). Used for navigation inside the app
 * when we already know the user is in the React shell.
 */
export const buildInAppUrl = (kind, id) => {
  if (!id) return PUBLIC_APP_URL;
  switch (kind) {
    case 'gim_toli':
      return `${PUBLIC_APP_URL}/feed?tab=gimtoli&post=${id}`;
    case 'short':
    case 'video':
      return `${PUBLIC_APP_URL}/feed?tab=videos&video=${id}`;
    case 'podcast':
      return `${PUBLIC_APP_URL}/feed?tab=studio&podcast=${id}`;
    case 'studio_story':
      return `${PUBLIC_APP_URL}/feed?tab=studio&story=${id}`;
    case 'article':
    case 'news':
      return `${PUBLIC_APP_URL}/explorer?article=${id}`;
    default:
      return `${PUBLIC_APP_URL}/${id}`;
  }
};

/**
 * Show the native share sheet (or fallbacks).
 *
 * @param {Object}  opts
 * @param {string}  opts.title    Short title shown in the share sheet
 * @param {string}  opts.text     Pre-filled message body
 * @param {string}  opts.url      The public URL to share
 * @param {string}  [opts.dialogTitle='Partager via']  Native dialog title (Android only)
 *
 * @returns {Promise<{ shared: boolean, cancelled?: boolean, method?: string }>}
 */
export async function shareContent({ title, text, url, dialogTitle = 'Partager via' }) {
  const safeTitle = title || 'Destine';
  const safeText = text || '';
  const safeUrl = url || PUBLIC_APP_URL;

  // 1. Native (iOS/Android via Capacitor) — most reliable, real share sheet
  if (Capacitor.isNativePlatform()) {
    try {
      await Share.share({
        title: safeTitle,
        text: safeText,
        url: safeUrl,
        dialogTitle,
      });
      return { shared: true, method: 'capacitor' };
    } catch (err) {
      // User dismissed the sheet → not an error.
      const msg = String(err?.message || err);
      if (/cancel|dismiss|user denied|abort/i.test(msg)) {
        return { shared: false, cancelled: true };
      }
      // Real failure → fall through to clipboard.
      console.warn('Capacitor Share failed, falling back to clipboard', err);
    }
  }

  // 2. Web Share API (mobile Safari / Chrome)
  if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
    try {
      await navigator.share({ title: safeTitle, text: safeText, url: safeUrl });
      return { shared: true, method: 'web-share' };
    } catch (err) {
      if (err?.name === 'AbortError') {
        return { shared: false, cancelled: true };
      }
      // Otherwise fall through to clipboard.
      console.warn('navigator.share failed, falling back to clipboard', err);
    }
  }

  // 3. Clipboard fallback
  try {
    const payload = safeText ? `${safeText}\n${safeUrl}` : safeUrl;
    await navigator.clipboard.writeText(payload);
    toast.success('Lien copié !');
    return { shared: true, method: 'clipboard' };
  } catch (err) {
    console.error('Clipboard write failed', err);
    toast.error('Impossible de partager');
    return { shared: false };
  }
}

/**
 * Bump the shares_count on the underlying document.
 * Returns the new count (or null on failure — never throws).
 */
export async function incrementShareCount(kind, id) {
  if (!id || !kind) return null;
  const k = ['short', 'video'].includes(kind) ? 'short' : kind;
  try {
    // We use fetch so we don't pull axios as a dependency here; the endpoint is public.
    const base = process.env.REACT_APP_BACKEND_URL || '';
    const res = await fetch(`${base}/api/share/increment/${k}/${id}`, { method: 'POST' });
    if (!res.ok) return null;
    const data = await res.json();
    return typeof data?.shares_count === 'number' ? data.shares_count : null;
  } catch (err) {
    console.warn('incrementShareCount failed', err);
    return null;
  }
}
